import React, {useEffect} from "react"
import ReactDOM from "react-dom/client"
import {I18nextProvider, useTranslation} from "react-i18next"

import {createTheme, ThemeProvider} from "@mui/material/styles"
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment"
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider"

import "./main.css"
import "./styles/tableWrapper.css"
import "./styles/iconStyle.css"
import "./styles/buttonStyle.css"
import "./assets/fonts/FontsFree-Net-Poppins-Light.ttf"
import "moment/dist/locale/fr"
import "moment/dist/locale/en-gb"
import {ApolloProvider} from "@apollo/client"
import {ReactKeycloakProvider} from "@react-keycloak/web"
import moment from "moment"

import apolloClient, {cache} from "./apolloClient"
import App from "./App"
import DocoonProvider from "./context/DocoonProvider"
import ErrorBoundary from "./utils/ErrorBoundary"
import i18n from "./utils/i18n"
import {createKeycloak} from "./utils/keycloak/keycloak"

const resolveMomentLocale = (lang: string | undefined): string =>
    (lang || "fr").toLowerCase().startsWith("en") ? "en-gb" : "fr"

// Activate the moment locale eagerly so month/year names use the chosen language
// from first render (AdapterMoment's adapterLocale alone does not switch moment globally).
moment.locale(resolveMomentLocale(i18n.language))

function LocalizedDatePickerProvider({children}: Readonly<{children: React.ReactNode}>) {
    const {i18n: i18nInstance} = useTranslation()
    const locale = resolveMomentLocale(i18nInstance.language)
    useEffect(() => {
        moment.locale(locale)
    }, [locale])
    return (
        <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={locale}>
            {children}
        </LocalizationProvider>
    )
}

const root = ReactDOM.createRoot(document.getElementById("root"))

const theme = createTheme({
    palette: {
        primary: {
            main: "#307ade"
        },
        secondary: {
            main: "#da4fa3"
        }
    }
})

fetch(globalThis.location.origin + "/settings.json")
    .then((response) => response.json())
    .then((settings) => {
        localStorage.setItem("appSettings", JSON.stringify(settings))
        root.render(
            <ReactKeycloakProvider
                authClient={createKeycloak(settings.KEYCLOAK_URL)}
                onTokens={(token) => {
                    localStorage.setItem("accessToken", token.token)
                    localStorage.setItem("idToken", token.idToken)
                }}
                initOptions={{
                    flow: "implicit",
                    onLoad: "check-sso"
                }}
            >
                <ApolloProvider client={apolloClient}>
                    <I18nextProvider i18n={i18n}>
                        <DocoonProvider cache={cache}>
                            <ErrorBoundary>
                                <ThemeProvider theme={theme}>
                                    <LocalizedDatePickerProvider>
                                        <App />
                                    </LocalizedDatePickerProvider>
                                </ThemeProvider>
                            </ErrorBoundary>
                        </DocoonProvider>
                    </I18nextProvider>
                </ApolloProvider>
            </ReactKeycloakProvider>
        )
    })
